import React, { useState, useRef, useCallback } from "react";
import ReactCrop, {
  Crop,
  PixelCrop,
  centerCrop,
  makeAspectCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { EntityType, validateFile, formatFileSize } from "../../config/uploads";
import { useMediaUpload } from "../../hooks/useMediaUpload";
import { LoadingSpinner } from "../ui/LoadingSpinner";
import { parseUploadError } from "../../utils/uploadErrorHandler";
import { isValidUUID } from "../../utils/isValidUUID";
import { startAsyncSpan } from "../../utils/sentry";
import { useAnalytics } from "../../hooks/useAnalytics";

interface ImageUploaderProps {
  entityType: EntityType;
  entityId: string;
  currentImageUrl?: string | null;
  onUploadComplete: (key: string, previewUrl?: string) => void;
  onUploadError?: (error: string) => void;
  onDeleteImage?: () => void;
  disabled?: boolean;
  className?: string;
  showBackgroundToggle?: boolean;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  entityType,
  entityId,
  currentImageUrl,
  onUploadComplete,
  onUploadError,
  onDeleteImage,
  disabled = false,
  className = "",
  showBackgroundToggle = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [showGreyBackground, setShowGreyBackground] = useState(false);

  // Crop functionality state
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [showCropModal, setShowCropModal] = useState(false);
  const [originalFile, setOriginalFile] = useState<File | null>(null);

  const { upload, isUploading, progress, error, resetState } = useMediaUpload();
  const analytics = useAnalytics();

  // Disable interactions if global disabled OR entityId is not a valid UUID yet
  const uploaderDisabled = disabled || !isValidUUID(entityId);

  // Check if this entity type requires 3:2 aspect ratio cropping
  const requiresCropping =
    entityType === "restaurant-header" || entityType === "facility-header";

  // Helper function to create a crop with 3:2 aspect ratio
  const createCrop = (img: HTMLImageElement): Crop => {
    const aspect = 3 / 2;
    return centerCrop(
      makeAspectCrop(
        {
          unit: "%",
          width: 90,
        },
        aspect,
        img.width,
        img.height
      ),
      img.width,
      img.height
    );
  };

  // Helper function to crop image to canvas
  const cropImageToCanvas = (
    image: HTMLImageElement,
    crop: PixelCrop,
    scale = 1,
    rotate = 0
  ): Promise<Blob> => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error("No 2d context");
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const pixelRatio = window.devicePixelRatio;

    canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
    canvas.height = Math.floor(crop.height * scaleY * pixelRatio);

    ctx.scale(pixelRatio, pixelRatio);
    ctx.imageSmoothingQuality = "high";

    const cropX = crop.x * scaleX;
    const cropY = crop.y * scaleY;

    const rotateRads = rotate * (Math.PI / 180);
    const centerX = image.naturalWidth / 2;
    const centerY = image.naturalHeight / 2;

    ctx.save();

    // Move the crop origin to the canvas origin (0,0)
    ctx.translate(-cropX, -cropY);
    // Move the origin to the center of the original position
    ctx.translate(centerX, centerY);
    // Rotate around the origin
    ctx.rotate(rotateRads);
    // Scale the image up or down
    ctx.scale(scale, scale);
    // Move the center of the image to the origin (0,0)
    ctx.translate(-centerX, -centerY);
    ctx.drawImage(
      image,
      0,
      0,
      image.naturalWidth,
      image.naturalHeight,
      0,
      0,
      image.naturalWidth,
      image.naturalHeight
    );

    ctx.restore();

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          }
        },
        "image/jpeg",
        0.9
      );
    });
  };

  const handleFileSelect = useCallback(
    async (file: File) => {
      if (uploaderDisabled) return;

      // Validate file
      const validation = validateFile(file);
      if (!validation.isValid) {
        onUploadError?.(validation.error || "Invalid file");
        return;
      }

      // If cropping is required, show crop modal
      if (requiresCropping) {
        setOriginalFile(file);
        setShowCropModal(true);

        // Create preview for cropping
        const reader = new FileReader();
        reader.onload = (event) => {
          const result = event.target?.result as string;
          setPreviewUrl(result);
        };
        reader.readAsDataURL(file);
        return;
      }

      // For non-cropping entity types, proceed with direct upload
      await uploadFile(file);
    },
    [uploaderDisabled, requiresCropping, onUploadError]
  );

  // Helper function to upload file
  const uploadFile = async (file: File) => {
    // Create preview and keep local copy for immediate callback usage
    let localPreview: string | null = null;
    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result as string;
      localPreview = result;
      setPreviewUrl(result);
    };
    reader.readAsDataURL(file);

    try {
      resetState();

      // Track file upload performance with Sentry
      const result = await startAsyncSpan(
        {
          op: "ui.upload",
          name: "Image Upload",
          attributes: {
            entityType,
            fileSize: file.size,
            fileName: file.name,
            fileType: file.type,
          },
        },
        async (span) => {
          span.setAttribute("entityId", entityId);
          return await upload(file, entityType, entityId);
        }
      );

      // Track successful upload for analytics
      analytics.upload(entityType, file.type, file.size, true);

      // Use localPreview if available, otherwise fallback to state value
      onUploadComplete(result.key, localPreview || previewUrl || undefined);
    } catch (error) {
      const errorInfo = parseUploadError(error);

      // Track failed upload for analytics
      analytics.upload(entityType, file.type, file.size, false);

      onUploadError?.(errorInfo.userMessage);
      setPreviewUrl(null);
    }
  };

  // Handle crop completion
  const handleCropComplete = useCallback(async () => {
    if (!completedCrop || !imgRef.current || !originalFile) {
      return;
    }

    try {
      const croppedBlob = await cropImageToCanvas(
        imgRef.current,
        completedCrop
      );
      const croppedFile = new File([croppedBlob], originalFile.name, {
        type: "image/jpeg",
      });

      // Close crop modal
      setShowCropModal(false);
      setCrop(undefined);
      setCompletedCrop(undefined);
      setOriginalFile(null);

      // Upload the cropped file
      await uploadFile(croppedFile);
    } catch (error) {
      onUploadError?.("Failed to crop image");
    }
  }, [completedCrop, originalFile, uploadFile, onUploadError]);

  // Handle crop cancel
  const handleCropCancel = useCallback(() => {
    setShowCropModal(false);
    setCrop(undefined);
    setCompletedCrop(undefined);
    setOriginalFile(null);
    setPreviewUrl(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleFileSelect(files[0]);
      }
    },
    [handleFileSelect]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFileSelect(files[0]);
      }
    },
    [handleFileSelect]
  );

  const handleDeleteClick = useCallback(() => {
    if (onDeleteImage) {
      // Close crop modal if open before deleting
      if (showCropModal) {
        setShowCropModal(false);
        setPreviewUrl(null);
      }
      onDeleteImage();
      setPreviewUrl(null);
    }
  }, [onDeleteImage, showCropModal]);

  const displayImageUrl = previewUrl || currentImageUrl;
  const hasImage = !!displayImageUrl;

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Upload / Preview Area */}
      <div
        className={`
          relative rounded-lg transition-colors
          ${
            hasImage && !isUploading
              ? "p-0 border-none"
              : `border-2 border-dashed p-6 text-center ${
                  isDragOver
                    ? "border-blue-400 bg-blue-50 dark:bg-blue-900/20"
                    : "border-gray-300 dark:border-gray-600"
                }`
          }
          ${
            uploaderDisabled
              ? "opacity-50 cursor-not-allowed"
              : "cursor-pointer hover:border-gray-400 dark:hover:border-gray-500"
          }
        `}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => !uploaderDisabled && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleInputChange}
          disabled={uploaderDisabled}
          className="hidden"
        />
        {isUploading ? (
          <div className="space-y-2">
            <LoadingSpinner size="lg" />
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Uploading...
            </p>
            {progress && (
              <div className="w-full bg-gray-200 rounded-full h-2 dark:bg-gray-700">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress.percentage}%` }}
                />
              </div>
            )}
            {progress && (
              <p className="text-xs text-gray-500">
                {progress.percentage}% ({formatFileSize(progress.loaded)} /{" "}
                {formatFileSize(progress.total)})
              </p>
            )}
          </div>
        ) : hasImage ? (
          /* Existing image preview with overlay */
          <div
            className={`relative group inline-block w-full rounded-lg overflow-hidden ${
              showGreyBackground
                ? "bg-gray-200 dark:bg-gray-700"
                : "bg-gray-50 dark:bg-gray-800"
            }`}
          >
            <div
              className={`w-full ${
                requiresCropping ? "aspect-[3/2]" : "h-64"
              } rounded-lg overflow-hidden`}
            >
              <img
                src={displayImageUrl as string}
                alt="Current"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity rounded-lg">
              <span className="px-3 py-1 bg-white/20 rounded-md backdrop-blur-md text-sm">
                Click to replace image
              </span>
            </div>

            {error && !isUploading && (
              <div className="absolute inset-0 bg-red-700/80 flex items-center justify-center text-white text-sm p-2 text-center">
                {error}
              </div>
            )}

            {onDeleteImage && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick();
                }}
                disabled={uploaderDisabled || isUploading}
                className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 shadow-lg transition-colors disabled:opacity-50"
                title="Remove image"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}

            {showBackgroundToggle && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowGreyBackground(!showGreyBackground);
                }}
                className={`absolute top-2 left-2 px-3 py-1.5 rounded-full shadow-lg transition-colors flex items-center gap-1.5 text-sm font-medium ${
                  showGreyBackground
                    ? "bg-black text-white hover:bg-gray-800"
                    : "bg-white text-black hover:bg-gray-50"
                }`}
                title={
                  showGreyBackground
                    ? "Hide grey background"
                    : "Show grey background"
                }
              >
                {showGreyBackground ? (
                  // Eye-slash icon (when activated)
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                    />
                  </svg>
                ) : (
                  // Eye icon (when not activated)
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                )}
                <span>Enhance clarity</span>
              </button>
            )}
          </div>
        ) : (
          /* No image yet placeholder */
          <div className="space-y-2 relative">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <p>
                <span className="font-medium text-blue-600 hover:text-blue-500">
                  Click to upload
                </span>{" "}
                or drag and drop
              </p>
              <p className="text-xs">PNG, JPG, WebP, GIF up to 10MB</p>
            </div>

            {error && !isUploading && (
              <div className="absolute inset-0 flex items-center justify-center bg-red-700/80 text-white text-sm p-2 text-center rounded-lg">
                {error}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Fallback Error Display below (for narrow layouts) */}
      {error && (
        <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded-md mt-2">
          {error}
        </div>
      )}

      {/* Crop Modal */}
      {showCropModal && previewUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Crop Image to 3:2 Aspect Ratio
              </h3>
              <button
                onClick={handleCropCancel}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="mb-4">
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={3 / 2}
                minWidth={200}
                minHeight={133}
              >
                <img
                  ref={imgRef}
                  alt="Crop me"
                  src={previewUrl}
                  style={{ maxHeight: "60vh", maxWidth: "100%" }}
                  onLoad={(e) => {
                    const img = e.currentTarget;
                    setCrop(createCrop(img));
                  }}
                />
              </ReactCrop>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCropCancel}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleCropComplete}
                disabled={!completedCrop}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-md"
              >
                Crop & Upload
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
